'use client';

import { cn } from '@zealot/ui/lib/utils';
import { Ellipsis } from 'lucide-react';
import * as React from 'react';

import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from './dropdown-menu';

type SimpleDropdownMenuItem = {
  label: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  variant?: 'default' | 'destructive';
  disabled?: boolean;
  shortcut?: string;
  group?: string;
};

type SimpleDropdownMenuProps = {
  items: SimpleDropdownMenuItem[];
  trigger?: React.ReactElement;
  triggerAriaLabel?: string;
  label?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  sideOffset?: number;
  className?: string;
  contentClassName?: string;
};

const renderItem = (item: SimpleDropdownMenuItem) => (
  <DropdownMenuItem key={item.label} onClick={item.onClick} variant={item.variant} disabled={item.disabled}>
    {item.icon}
    {item.label}
    {item.shortcut && <DropdownMenuShortcut>{item.shortcut}</DropdownMenuShortcut>}
  </DropdownMenuItem>
);

const SimpleDropdownMenu = ({
  items,
  trigger,
  triggerAriaLabel = 'More options',
  label,
  align = 'end',
  side,
  sideOffset,
  className,
  contentClassName,
}: SimpleDropdownMenuProps) => {
  const hasGroups = items.some((item) => item.group);

  const groupedItems = React.useMemo(() => {
    if (!hasGroups) return null;
    const groups = new Map<string, SimpleDropdownMenuItem[]>();
    for (const item of items) {
      const key = item.group ?? '';
      const existing = groups.get(key);
      if (existing) {
        existing.push(item);
      } else {
        groups.set(key, [item]);
      }
    }
    return groups;
  }, [items, hasGroups]);

  const renderItems = () => {
    if (groupedItems) {
      const entries = [...groupedItems.entries()];
      return entries.map(([groupName, groupItems], idx) => (
        <React.Fragment key={groupName}>
          {idx > 0 && <DropdownMenuSeparator />}
          <DropdownMenuGroup>
            {groupName && <DropdownMenuLabel>{groupName}</DropdownMenuLabel>}
            {groupItems.map(renderItem)}
          </DropdownMenuGroup>
        </React.Fragment>
      ));
    }
    return items.map(renderItem);
  };

  return (
    <DropdownMenu>
      {trigger ? (
        <DropdownMenuTrigger
          render={(props) => React.cloneElement(trigger as React.ReactElement, props as Record<string, unknown>)}
        />
      ) : (
        <DropdownMenuTrigger
          render={(props) => (
            <Button {...props} variant="ghost" size="icon-sm" aria-label={triggerAriaLabel} className={cn(className)}>
              <Ellipsis />
            </Button>
          )}
        />
      )}
      <DropdownMenuContent align={align} side={side} sideOffset={sideOffset} className={contentClassName}>
        {label && (
          <DropdownMenuGroup>
            <DropdownMenuLabel>{label}</DropdownMenuLabel>
            <DropdownMenuSeparator />
          </DropdownMenuGroup>
        )}
        {renderItems()}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export type { SimpleDropdownMenuItem, SimpleDropdownMenuProps };
export { SimpleDropdownMenu };
